/**
 * Default SEO copy for static (non-CMS) pages.
 *
 * Used as:
 *  - The sample tokens in the CMS SEO panel preview (so editors see the real
 *    page text rather than "Sample article title").
 *  - The fallback title/description in <SeoHead> when no DB template override
 *    exists for that page.
 *
 * Keep keys in sync with the `collectionKey="page.*"` props passed to
 * <SeoHead> across src/pages/*.
 */

export interface StaticPageSeo {
  title: string;
  description: string;
}

export const SITE_DEFAULT_TITLE =
  "deepc - Clinical AI Infrastructure for Health Systems";
export const SITE_DEFAULT_DESCRIPTION =
  "Clinical AI infrastructure for health systems to deploy, monitor, and govern AI workflows across existing clinical and operational systems.";

export const STATIC_PAGE_SEO: Record<string, StaticPageSeo> = {
  "page.home": {
    title: SITE_DEFAULT_TITLE,
    description:
      "deepc helps health systems connect once, then deploy, monitor, and govern clinical AI workflows across existing systems and sites.",
  },
  "page.solution": {
    title: "Clinical AI Infrastructure for Health Systems - deepc",
    description:
      "See how deepc helps health systems build, deploy, monitor, and govern AI workflows on one reusable infrastructure layer.",
  },
  "page.company": {
    title: "Company - deepc Clinical AI Infrastructure",
    description:
      "Meet the team building clinical AI infrastructure for health systems deploying safe, scalable, governed AI workflows.",
  },
  "page.careers": {
    title: "Careers - Build Clinical AI Infrastructure at deepc",
    description:
      "Join deepc to build the infrastructure layer helping health systems deploy, monitor, and govern clinical AI safely.",
  },
  "page.contact": {
    title: "Contact deepc - Clinical AI Infrastructure",
    description:
      "Talk to deepc about deploying, monitoring, and governing clinical AI workflows across your health system.",
  },
  "page.faq": {
    title: "Clinical AI Infrastructure FAQ - deepc",
    description:
      "Answers for health system leaders evaluating clinical AI infrastructure, including integration, security, governance, value, and deployment.",
  },
  "page.insights": {
    title: "Clinical AI Insights, Evidence, and News - deepc",
    description:
      "Read deepc insights on clinical AI infrastructure, governance, adoption, evidence, deployment, and health system transformation.",
  },
  "page.events": {
    title: "Clinical AI Events and Webinars - deepc",
    description:
      "Explore deepc events, webinars, and conferences on clinical AI infrastructure, adoption, governance, and deployment.",
  },
  "page.leadership_faq": {
    title: "Clinical AI Leadership FAQ - deepc",
    description:
      "Strategic answers for executives, clinical leaders, and IT teams evaluating clinical AI infrastructure and governance.",
  },
  "page.release_notes": {
    title: "Release Notes - deepc Clinical AI Infrastructure",
    description:
      "See product updates, improvements, and releases for the deepc clinical AI infrastructure platform.",
  },
  "page.thank_you": {
    title: "Thank You - deepc",
    description:
      "Thanks for contacting deepc. Our team will follow up about your clinical AI infrastructure needs shortly.",
  },
};

export function getStaticPageSeo(collectionKey?: string): StaticPageSeo {
  if (collectionKey && STATIC_PAGE_SEO[collectionKey]) {
    return STATIC_PAGE_SEO[collectionKey];
  }
  return { title: SITE_DEFAULT_TITLE, description: SITE_DEFAULT_DESCRIPTION };
}
